import type { TenantContext } from '~/types/types'

const FALLBACK_PRIMARY = '#7c3aed'
const FALLBACK_SECONDARY = '#0ea5e9'
const FALLBACK_ACCENT = '#22c55e'

const LIGHT_BG: RGB = [255, 255, 255]
const DARK_BG: RGB = [15, 14, 23]
const BLACK: RGB = [0, 0, 0]
const WHITE: RGB = [255, 255, 255]

type RGB = [number, number, number]

const hexToRgb = (hex: string): RGB | null => {
  const raw = hex.replace('#', '').trim()
  const full = raw.length === 3 ? raw.split('').map(ch => ch + ch).join('') : raw
  if (!/^[0-9a-f]{6}$/i.test(full)) return null
  const r = Number.parseInt(full.slice(0, 2), 16)
  const g = Number.parseInt(full.slice(2, 4), 16)
  const b = Number.parseInt(full.slice(4, 6), 16)
  return [r, g, b]
}

const relLum = ([r, g, b]: RGB): number => {
  const lin = (c: number) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

const contrast = (a: RGB, b: RGB): number => {
  const la = relLum(a)
  const lb = relLum(b)
  const [hi, lo] = la > lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}

// sRGB linear mix — approximates CSS `color-mix(in oklab, ...)` closely enough
// for a contrast guard. CSS does the real perceptual mix at render time.
const mix = (a: RGB, b: RGB, aRatio: number): RGB => [
  Math.round(a[0] * aRatio + b[0] * (1 - aRatio)),
  Math.round(a[1] * aRatio + b[1] * (1 - aRatio)),
  Math.round(a[2] * aRatio + b[2] * (1 - aRatio)),
]

/**
 * Validates that a brand primary will produce legible derived accents in BOTH
 * light and dark modes. Mirrors the color-mix derivations in main.css:
 *   light --accent2 = mix(primary 70%, black) — must read on white
 *   dark  --accent  = mix(primary 75%, white) — must read on dark bg
 * Both must clear WCAG AA (≥ 4.5:1) for normal-weight text.
 */
export const validateBrandPair = (primaryHex: string): boolean => {
  const rgb = hexToRgb(primaryHex)
  if (!rgb) return false
  const lightAccent2 = mix(rgb, BLACK, 0.7)
  const darkAccent = mix(rgb, WHITE, 0.75)
  return contrast(lightAccent2, LIGHT_BG) >= 4.5 && contrast(darkAccent, DARK_BG) >= 4.5
}

export const useTenantTheme = () => {
  const apply = (tenant: TenantContext | null) => {
    if (!import.meta.client) return

    const root = document.documentElement
    const branding = tenant?.branding

    const primaryRaw = branding?.primary_color || FALLBACK_PRIMARY
    const secondary = branding?.secondary_color || FALLBACK_SECONDARY
    const accent = branding?.accent_color || FALLBACK_ACCENT

    const primary = validateBrandPair(primaryRaw) ? primaryRaw : FALLBACK_PRIMARY

    root.style.setProperty('--tenant-primary', primary)
    root.style.setProperty('--tenant-secondary', secondary)
    root.style.setProperty('--tenant-accent', accent)
  }

  return { apply }
}
